import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Table, Empty, Typography, Button } from 'antd';
import type { TableColumnsType } from 'antd';
import {
  selectors,
  recomputeLeaderboard,
  METRICS_TYPES,
  LeaderboardScoresType,
} from 'state';
import { formatters, useModal } from 'services';
import EditWeightsModal from '../../Performance/editWeightsModal';

// Fixed column widths (module scope so the columns memo stays dep-stable). The
// drawer width is derived from these so the table never scrolls left-to-right.
const COL_PLAYER = 120;
const COL_SCORE = 140;
const COL_METRIC = 160;

/**
 * Left-edge slide-in leaderboard for the studio. A persistent "nipple" handle
 * rides the panel's right edge; clicking it slides the leaderboard in/out. The
 * panel grows to fit every column (no horizontal scrollbar).
 *
 * Plumbing carried over from the Performance tab's <LeaderBoard>: the rows
 * (`session.leaderboard_scores`) are already fetched by useStudio2Data's
 * `fetchLeaderboard` dispatch (it also drives the rail ranks), so the drawer is
 * a pure reader. The same column set is reproduced, with percentage formatting
 * for metric scores and a percentage-point number for the total score, plus the
 * Recompute and Edit Weights actions (the latter opening the shared Performance
 * EditWeightsModal).
 */
const LeaderboardDrawer: React.FC = () => {
  const dispatch = useDispatch();
  const session = useSelector(selectors.activeSession);
  const scores: LeaderboardScoresType[] = session?.leaderboard_scores ?? [];

  const [open, setOpen] = useState(false);
  const [editWeightsModal, showEditWeightsModal] = useModal(EditWeightsModal);

  // Metric columns are derived from the first row's score breakdown (same as
  // the Performance leaderboard).
  const metricTypes = useMemo(() => {
    const first = scores[0];
    if (!first) return [];
    return Array.from(new Set(first.scores.map(s => s.metric_type)));
  }, [scores]);

  // Don't render half-built rows before the metric breakdown has arrived.
  const dataSource = metricTypes.length ? scores : [];

  // Size the panel to the full table so it never needs a horizontal scrollbar;
  // it widens with the metric-column count.
  const tableWidth = COL_PLAYER + COL_SCORE + COL_METRIC * metricTypes.length;
  const panelWidth = Math.max(460, tableWidth + 50);

  const columns: TableColumnsType<LeaderboardScoresType> = useMemo(() => {
    const base: TableColumnsType<LeaderboardScoresType> = [
      {
        title: 'Player',
        dataIndex: 'player_name',
        key: 'player_name',
        fixed: 'left',
        width: COL_PLAYER,
      },
      {
        title: 'Total Score',
        dataIndex: 'total_score',
        key: 'total_score',
        width: COL_SCORE,
        render: (val: number) => formatters.commasExact2Digits(val * 100),
        sorter: (a, b) => a.total_score - b.total_score,
        defaultSortOrder: 'descend',
      },
    ];
    const metricCols: TableColumnsType<LeaderboardScoresType> = metricTypes.map(
      metricType => ({
        title: METRICS_TYPES[metricType] ?? `Metric ${metricType}`,
        key: `metric_${metricType}`,
        width: COL_METRIC,
        render: (_: unknown, record: LeaderboardScoresType) => {
          const metric = record.scores.find(s => s.metric_type === metricType);
          return metric ? formatters.percent2Digit(metric.score) : '-';
        },
      }),
    );
    return [...base, ...metricCols];
  }, [metricTypes]);

  return (
    <div
      className={`studio2__lb ${open ? 'studio2__lb--open' : ''}`}
      style={{ '--s2-lb-width': `${panelWidth}px` } as React.CSSProperties}
    >
      <div className="studio2__lb-panel">
        <div className="studio2__lb-head">
          <span className="studio2__lb-title">Leaderboard</span>
          <div className="studio2__lb-actions">
            <Button size="small" onClick={() => showEditWeightsModal({})}>
              Edit Weights
            </Button>
            <Button
              size="small"
              onClick={() =>
                session?.id != null &&
                dispatch(recomputeLeaderboard({ session_id: session.id }))
              }
            >
              Recompute
            </Button>
            <Button
              size="small"
              type="text"
              aria-label="Hide leaderboard"
              onClick={() => setOpen(false)}
            >
              ✕
            </Button>
          </div>
        </div>
        <div className="studio2__lb-body">
          <Table
            size="small"
            rowKey="player_id"
            dataSource={dataSource}
            columns={columns}
            bordered
            pagination={false}
            scroll={{ x: tableWidth }}
            locale={{
              emptyText: (
                <Empty
                  description={
                    <Typography.Text type="secondary">
                      Leaderboard not available yet.
                    </Typography.Text>
                  }
                />
              ),
            }}
          />
        </div>
      </div>

      {/* Persistent edge handle — always visible, rides the panel's edge. */}
      <button
        type="button"
        className="studio2__lb-handle"
        aria-label={open ? 'Hide leaderboard' : 'Show leaderboard'}
        onClick={() => setOpen(o => !o)}
      >
        <span className="studio2__lb-handle-label">Leaderboard</span>
        <span className="studio2__lb-handle-caret">{open ? '‹' : '›'}</span>
      </button>

      {editWeightsModal}
    </div>
  );
};

export default LeaderboardDrawer;
